import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import type { CommandPaletteBookReferenceResult } from 'types/commandPaletteBookReference';
import { parseCommandPaletteBookReferenceQuery } from 'utils/parseCommandPaletteBookReferenceQuery';

type BookReferenceSearchStatus = 'idle' | 'loading' | 'success' | 'error';

interface UseCommandPaletteBookReferenceSearchResult {
  parsedQuery: ReturnType<typeof parseCommandPaletteBookReferenceQuery>;
  results: CommandPaletteBookReferenceResult[];
  status: BookReferenceSearchStatus;
  awaitingDigits: boolean;
}

export function useCommandPaletteBookReferenceSearch(
  query: string,
  isOpen: boolean,
  canAccessPath: (path: string) => boolean,
): UseCommandPaletteBookReferenceSearchResult {
  const activeCompanyId = useActiveCompanyStore(
    (state) => state.activeCompanyId,
  );
  const parsedQuery = useMemo(
    () => parseCommandPaletteBookReferenceQuery(query),
    [query],
  );
  const [results, setResults] = useState<CommandPaletteBookReferenceResult[]>(
    [],
  );
  const [status, setStatus] = useState<BookReferenceSearchStatus>('idle');

  const awaitingDigits =
    parsedQuery !== null && parsedQuery.sequencePart.length === 0;

  const runSearch = useCallback(async () => {
    if (!parsedQuery || !activeCompanyId) {
      setResults([]);
      setStatus('idle');
      return;
    }

    if (!canAccessPath(parsedQuery.handler.accessPath)) {
      setResults([]);
      setStatus('idle');
      return;
    }

    if (parsedQuery.sequencePart.length === 0) {
      setResults([]);
      setStatus('idle');
      return;
    }

    setStatus('loading');
    try {
      const nextResults = await parsedQuery.handler.search({
        companyId: activeCompanyId,
        referenceQuery: parsedQuery.normalizedQuery,
        prefix: parsedQuery.prefix,
        sequencePart: parsedQuery.sequencePart,
      });
      setResults(nextResults);
      setStatus('success');
    } catch {
      setResults([]);
      setStatus('error');
    }
  }, [activeCompanyId, canAccessPath, parsedQuery]);

  const debouncedRunSearch = useDebouncedCallback(runSearch, 300);

  useEffect(() => {
    if (!isOpen) {
      setResults([]);
      setStatus('idle');
      return;
    }

    debouncedRunSearch();
  }, [debouncedRunSearch, isOpen, query]);

  return {
    parsedQuery,
    results,
    status,
    awaitingDigits,
  };
}
